import React from 'react';
import autoBind from 'react-autobind';
import Localization from "../Localization.jsx";
import Config from "../Config.jsx";
import AuthService from "../tools/AuthService.js";
import { renderGoogleButton } from "../tools/googleIdentity.js";
import { signInWithApple, isUserCancelled } from "../tools/appleIdentity.js";
import {
  BIND_MESSAGE_TYPE,
  BIND_CHANNEL_NAME,
  BIND_STORAGE_KEY,
  BIND_WINDOW_NAME,
  bindReturnUrl,
  bindOutcome,
  bindPasswordGateKey,
  bindAlreadyLinkedKey,
  bindFinishOutcome,
} from "../tools/edconnectBind.js";
import AppleSignInButton from "./AppleSignInButton.jsx";
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography, 
  Box, 
  Paper, 
  IconButton,
  Chip,
  CircularProgress,
  Divider,
  TextField
} from '@mui/material';
import { 
  Link as LinkIcon, 
  LinkOff as LinkOffIcon, 
  Delete as DeleteIcon,
  Add as AddIcon,
  AccountCircle as AccountIcon
} from '@mui/icons-material';

/** Human label for a provider row. */
const PROVIDER_LABELS = {
  google: 'Google',
  apple: 'Apple',
  edconnect: 'EdConnect',
};

/**
 * How long to keep waiting on a bind popup before giving the dialog back.
 *
 * The server expires the OAuth state after 10 minutes. Past that the callback
 * cannot tell the round trip was a BIND at all, so it falls back to the app
 * flow and redirects the popup to the API host's own failure page - a DIFFERENT
 * ORIGIN, from which nothing can signal this tab and which we cannot close. A
 * user who leaves EdCity's login sitting open that long would otherwise be
 * waiting on a window that is never coming back.
 */
const BIND_TIMEOUT_MS = 12 * 60 * 1000;

/**
 * How long to disbelieve `popup.closed` after opening.
 *
 * A Cross-Origin-Opener-Policy on the serving host severs the handle, and a
 * severed handle reports closed === true immediately. Treating that as "the
 * user cancelled" would abort every bind on the first tick.
 */
const BIND_CLOSE_GRACE_MS = 1500;

/**
 * What a refused bind_start means. `password` is the one failure the user can
 * act on — the server now re-proves the account's own password before it will
 * start a link, exactly as it does for Google and Apple — so it gets the
 * message that already exists for those two rather than the generic one.
 */
function startErrorKey(res) {
  if (res && res.error === 'password') {
    return 'LINK_PASSWORD_REQUIRED';
  }
  if (res && res.error === 'account_already_linked') {
    // The server refuses a second EdCity link at the START leg too, so a stale
    // tab whose account was linked elsewhere since it loaded gets the message
    // that names the fix rather than the generic failure.
    return 'EDCONNECT_BIND_ACCOUNT_HAS_ONE';
  }
  return 'EDCONNECT_BIND_FAILED';
}

class EDConnectDialog extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      edconnectAccounts: [],
      hasAccounts: false,
      // Provider links from the NEW surface (GET /api/auth/links) — Google and
      // Apple live here; the edconnect rows above stay on the legacy cmds so
      // nothing regresses while both exist.
      apiLinks: [],
      linkPassword: '',
      googleReady: false,
      // A bind round trip is in flight. Deliberately NOT the `loading` flag
      // above: that one is written by three unrelated operations and replaces
      // the whole dialog body with a spinner, which is how a cancelled bind
      // used to leave the dialog with nothing in it but a dead spinner.
      binding: false,
    };
    this.googleLinkContainer = React.createRef();
    // Bind round-trip bookkeeping. Instance fields, not state: nothing here is
    // rendered, and a re-render per poll tick would be pure waste.
    this.bindPopup = null;
    this.bindChannel = null;
    this.bindPoll = null;
    this.bindTimer = null;
    this.bindOpenedAt = 0;
    this.bindSettled = true;
    // A finish call is in flight. Three delivery channels carry the same
    // payload and the ticket is single-use, so the second one must not spend it.
    this.bindFinishing = false;
    autoBind(this);
  }

  componentDidMount() {
    // The ONLY hook that can rescue the same-tab fallback below. See
    // handlePageShow for why nothing else runs after that navigation.
    window.addEventListener('pageshow', this.handlePageShow);
  }

  componentWillUnmount() {
    window.removeEventListener('pageshow', this.handlePageShow);
    this.teardownBindWatch();
  }

  /**
   * A page restored from the back-forward cache, i.e. the user pressed BACK.
   *
   * This is the whole reason the spinner used to stick. When a bind navigates
   * this tab to EdCity and the user cancels there and comes back, the browser
   * does NOT re-run the app: it restores the document from bfcache with the
   * entire JS heap frozen as it left - `binding` still true, the dialog still
   * open - and no code runs afterwards to say the trip is over. There are no
   * `?edconnect_bind=` markers on that URL either (a BACK navigation is not the
   * server's redirect), so RootPage never gets a look in.
   *
   * `persisted` is what distinguishes a bfcache restore from an ordinary load;
   * an ordinary load remounts everything and needs no help.
   */
  handlePageShow(e) {
    if (!e || !e.persisted) {
      return;
    }
    // Any request that was in flight when the document was frozen is dead, so
    // both flags are stale by definition.
    this.teardownBindWatch();
    this.bindSettled = true;
    this.bindFinishing = false;
    this.setState({ binding: false, loading: false });
  }

  componentDidUpdate(prevProps) {
    // When dialog opens, load EDConnect accounts
    if (this.props.open && !prevProps.open) {
      // The legacy EdConnect list is only worth fetching when its half of the
      // dialog is shown; the new links load either way.
      if (typeof Config.ENABLE_EDCONNECT_BINDING === 'function'
          && Config.ENABLE_EDCONNECT_BINDING()) {
        this.loadEDConnectAccounts();
      }
      this.loadApiLinks();
    }
    // Mount the GIS button once its container exists (open + not yet mounted).
    if (this.props.open && !this.state.googleReady
        && this.googleLinkContainer.current
        && typeof Config.ENABLE_GOOGLE_BINDING === 'function'
        && Config.ENABLE_GOOGLE_BINDING()) {
      this.setState({ googleReady: true });
      renderGoogleButton(this.googleLinkContainer.current, this.handleGoogleLinkCredential, {
        locale: Localization.strings.getLanguage() === 'tc' ? 'zh-TW' : 'en',
      })
        .catch((e) => console.warn('EDConnectDialog: GIS failed to load', e));
    }
    if (!this.props.open && prevProps.open) {
      // Re-mount the GIS button next open — the dialog contents are unmounted
      // on close — and drop the typed password with the dialog that asked for
      // it. This used to hang off `googleReady`, which is only ever true when
      // Google binding is ON, so with it off the password stayed in state and
      // came back pre-filled the next time the dialog opened.
      this.setState({ googleReady: false, linkPassword: '' });
    }
  }

  loadApiLinks() {
    AuthService.listLinks().then((links) => {
      this.setState({ apiLinks: Array.isArray(links) ? links : [] });
    });
  }

  handleGoogleLinkCredential(idToken) {
    // The server demands the password for any account that has one — sending
    // it here just makes the failure mode a clear message instead of a 403.
    AuthService.linkGoogle(idToken, this.state.linkPassword)
      .then((result) => {
        if (result.ok) {
          this.props.root.addNotification(Localization.strings.LINK_GOOGLE_DONE, true);
          this.setState({ linkPassword: '' });
          this.loadApiLinks();
        } else if (result.status === 409) {
          this.props.root.addNotification(Localization.strings.LINK_GOOGLE_CONFLICT, false);
        } else if (result.status === 403) {
          this.props.root.addNotification(Localization.strings.LINK_PASSWORD_REQUIRED, false);
        } else {
          this.props.root.addNotification(Localization.strings.LINK_GOOGLE_FAILED, false);
        }
      });
  }

  handleUnlinkApi(linkId) {
    AuthService.unlinkAccount(linkId).then((ok) => {
      if (ok) {
        this.props.root.addNotification(Localization.strings.LINK_REMOVED, true);
        this.loadApiLinks();
      }
    });
  }

  loadEDConnectAccounts() {
    console.log('loadEDConnectAccounts called');
    console.log('API reference:', this.props.root.refs.API);
    
    this.setState({ loading: true });
    
    if (!this.props.root.refs.API) {
      console.error('API reference not available');
      this.setState({ loading: false });
      this.props.root.addNotification('API not available', false);
      return;
    }
    
    console.log('Making API request: get_edconnect_accounts');
    this.props.root.refs.API.request(
      "get_edconnect_accounts", 
      {}, 
      this.loadEDConnectAccountsOK, 
      this.loadEDConnectAccountsFail
    );
  }

  loadEDConnectAccountsOK(res) {
    console.log('EDConnect accounts response:', res);
    console.log('Response body:', res?.body);
    console.log('Response result:', res?.body?.result);
    
    if (res && res.body && Array.isArray(res.body.result)) {
      console.log('EDConnect accounts found:', res.body.result);
      console.log('Account count:', res.body.result.length);
      this.setState({
        loading: false,
        edconnectAccounts: res.body.result,
        hasAccounts: res.body.result.length > 0
      });
    } else {
      console.log('No EDConnect accounts found or unexpected response structure');
      console.log('Setting empty accounts array');
      this.setState({
        loading: false,
        edconnectAccounts: [],
        hasAccounts: false
      });
    }
  }

  loadEDConnectAccountsFail(err) {
    console.error('Failed to load EDConnect accounts:', err);
    console.error('Error details:', err);
    this.setState({ loading: false });
    this.props.root.addNotification('Failed to load EDConnect accounts: ' + (err?.message || 'Unknown error'), false);
  }

  handleAddAccount() {
    // Two steps, and the order is the whole point. The bearer proves WHICH
    // account this link attaches to, and a top-level navigation cannot carry
    // one — so we POST first (authenticated) to have the server seal our user
    // id into its own state, then send the browser to the URL it returns.
    //
    // The PHP tried to do this in one hop with `?binding=1`, and the flag was
    // never read, so composer binding has never worked in production.
    //
    // The trip now happens in a SEPARATE WINDOW. Sending this tab to EdCity
    // meant every bind — including an abandoned one — tore down the composer,
    // and coming back reloaded the whole app just to read two query markers.
    if (this.state.binding) {
      return;
    }

    // One EdCity account per EduVenture-AI account (server rule, 2026-09-17).
    // The button that leads here is hidden once a link exists, so reaching this
    // means a stale dialog — a second tab bound an account, or the link list
    // arrived after the click. Refusing here keeps a stale tab from burning a
    // full EdCity round trip on a bind the server will refuse anyway.
    const linkedGate = bindAlreadyLinkedKey({ linked: this.edconnectLinked() });
    if (linkedGate) {
      this.props.root.addNotification(Localization.strings[linkedGate], false);
      return;
    }

    // The server re-proves the account's own password before it will start a
    // link, so an empty box cannot succeed. Refusing it HERE, before the popup,
    // is the difference between a sentence the teacher can act on and a window
    // that opens, flashes and dies on a 403. Synchronous, so window.open below
    // still runs inside the click's transient activation.
    const gate = bindPasswordGateKey({
      hasPassword: this.props.hasPassword,
      password: this.state.linkPassword,
    });
    if (gate) {
      this.props.root.addNotification(Localization.strings[gate], false);
      return;
    }

    // window.open MUST be the first statement of the click handler. A popup is
    // only allowed while the click's transient activation is alive, and the
    // bind-start POST below is asynchronous — opening after it resolves is a
    // blocked popup every single time. So the window is opened blank and
    // pointed at EdCity once the server has answered.
    const popup = window.open(
      '', BIND_WINDOW_NAME,
      'width=520,height=680,menubar=no,toolbar=no,location=yes,status=no,resizable=yes,scrollbars=yes');

    if (!popup) {
      // Blocked, or a browser that refuses popups outright. Fall back to
      // exactly what this did before.
      this.startBindInThisTab();
      return;
    }

    this.bindPopup = popup;
    this.bindSettled = false;
    this.setState({ binding: true });
    this.armBindWatch();

    AuthService.startEdCityBind(bindReturnUrl(), this.state.linkPassword)
      .then((res) => {
        if (this.bindSettled) {
          // The user closed the popup while the POST was still out.
          return;
        }
        if (res && res.authorizeUrl) {
          // `replace`, not assignment: the blank page must not become a back
          // entry inside the popup.
          popup.location.replace(res.authorizeUrl);
          return;
        }
        this.settleBind({ ok: false, stringKey: startErrorKey(res) });
      })
      .catch(() => {
        this.settleBind({ ok: false, stringKey: 'EDCONNECT_BIND_FAILED' });
      });
  }

  /**
   * The pre-existing behaviour, kept for when the popup is blocked.
   *
   * The return URL differs from the popup branch on purpose and the two must
   * NOT be unified: this leg comes back to the composer's own URL so RootPage
   * can read the markers on the next mount, while the popup leg comes back to
   * the static relay page. Sending this leg to the relay page would strand the
   * user on it; sending the popup leg here would boot a second copy of the SPA
   * inside a 520px window.
   */
  startBindInThisTab() {
    this.setState({ binding: true });
    AuthService.startEdCityBind(window.location.href, this.state.linkPassword)
      .then((res) => {
        if (res && res.authorizeUrl) {
          window.location.href = res.authorizeUrl;
          return;
        }
        this.setState({ binding: false });
        this.props.root.addNotification(
          Localization.strings[startErrorKey(res)], false);
      })
      .catch(() => {
        this.setState({ binding: false });
        this.props.root.addNotification(
          Localization.strings.EDCONNECT_BIND_FAILED, false);
      });
  }

  /** Start listening for the popup's verdict, on every channel it may use. */
  armBindWatch() {
    this.bindOpenedAt = new Date().getTime();
    window.addEventListener('message', this.handleBindMessage);
    window.addEventListener('storage', this.handleBindStorage);
    try {
      if (typeof BroadcastChannel === 'function') {
        this.bindChannel = new BroadcastChannel(BIND_CHANNEL_NAME);
        this.bindChannel.onmessage = this.handleBindChannelMessage;
      }
    } catch (e) {
      this.bindChannel = null;
    }
    this.bindPoll = window.setInterval(this.pollBindPopup, 500);
    this.bindTimer = window.setTimeout(this.giveUpBind, BIND_TIMEOUT_MS);
  }

  teardownBindWatch() {
    window.removeEventListener('message', this.handleBindMessage);
    window.removeEventListener('storage', this.handleBindStorage);
    if (this.bindChannel) {
      try { this.bindChannel.close(); } catch (e) { /* already closed */ }
      this.bindChannel = null;
    }
    if (this.bindPoll) {
      window.clearInterval(this.bindPoll);
      this.bindPoll = null;
    }
    if (this.bindTimer) {
      window.clearTimeout(this.bindTimer);
      this.bindTimer = null;
    }
    const popup = this.bindPopup;
    this.bindPopup = null;
    if (popup) {
      try {
        if (!popup.closed) {
          popup.close();
        }
      } catch (e) { /* severed handle — the window closes itself */ }
    }
  }

  handleBindMessage(e) {
    // Same-origin only. The relay page is ours; anything else talking to this
    // window is not to be believed.
    if (!e || e.origin !== window.location.origin) {
      return;
    }
    this.handleBindPayload(e.data);
  }

  handleBindChannelMessage(e) {
    // A BroadcastChannel is same-origin by construction, so there is no origin
    // to check here.
    this.handleBindPayload(e && e.data);
  }

  handleBindStorage(e) {
    if (!e || e.key !== BIND_STORAGE_KEY || !e.newValue) {
      return;
    }
    try {
      this.handleBindPayload(JSON.parse(e.newValue).payload);
    } catch (err) { /* someone else's value */ }
  }

  handleBindPayload(payload) {
    if (!payload || payload.type !== BIND_MESSAGE_TYPE) {
      return;
    }
    const outcome = bindOutcome(payload);
    if (outcome.pending) {
      // The round trip only VERIFIED the identity — the link itself is a
      // separate authenticated POST, because a redirect cannot prove which
      // browser is finishing it. See AuthService.finishEdCityBind.
      //
      // Deliberately NOT settled here: `binding` has to stay true across that
      // call, because the round trip is not over until the link is written.
      // finishBind stands the popup watch down itself — see stopBindPolling.
      this.finishBind(outcome.bindCode);
      return;
    }
    this.settleBind(outcome);
  }

  /**
   * Redeem the bind ticket. The ONLY place the link is actually written.
   *
   * `bindFinishing` stops a second channel (postMessage, BroadcastChannel and
   * the storage event all deliver the same payload) spending the ticket twice —
   * it is single-use, so the second attempt would 410 and report a failure over
   * a bind that had just succeeded.
   */
  finishBind(bindCode) {
    if (this.bindFinishing) {
      return;
    }
    this.bindFinishing = true;
    // The relay page closes ITS OWN window a moment after reporting, so
    // `popup.closed` goes true within a tick or two of getting here — and
    // pollBindPopup reads that as "the user walked away" and cancels. Stand the
    // poll and the delivery channels down before that can fire.
    this.stopBindPolling();
    AuthService.finishEdCityBind(bindCode)
      .then((result) => {
        this.bindFinishing = false;
        this.settleBind(bindFinishOutcome(result));
        // Cancel pressed mid-request: settleBind returns early, but the server
        // committed anyway, so the list it refreshed a moment ago is now wrong.
        // Refreshing again is the difference between "the bind did nothing" and
        // "the bind worked and you stopped watching".
        this.loadApiLinks();
      })
      .catch(() => {
        this.bindFinishing = false;
        this.settleBind({ ok: false, stringKey: 'EDCONNECT_BIND_FAILED' });
      });
  }

  /**
   * Stand down the popup watch WITHOUT settling.
   *
   * The window is about to close itself, which pollBindPopup reads as "the user
   * walked away". The channels go too: the ticket has been taken, so a
   * duplicate payload arriving late has nothing left to do.
   *
   * The GIVE-UP TIMER deliberately stays armed. It is the only thing that can
   * end a finish call that never answers — authFetch has no timeout — and
   * without it a stalled request leaves the dialog spinning until the user
   * finds Cancel. Firing it late is harmless: settleBind refreshes the link
   * list either way, so a bind that did commit still shows up.
   */
  stopBindPolling() {
    if (this.bindPoll) {
      window.clearInterval(this.bindPoll);
      this.bindPoll = null;
    }
    window.removeEventListener('message', this.handleBindMessage);
    window.removeEventListener('storage', this.handleBindStorage);
    if (this.bindChannel) {
      try { this.bindChannel.close(); } catch (e) { /* already closed */ }
      this.bindChannel = null;
    }
  }

  /** The popup went away without reporting — the user closed it. */
  pollBindPopup() {
    const popup = this.bindPopup;
    if (!popup) {
      return;
    }
    if (new Date().getTime() - this.bindOpenedAt < BIND_CLOSE_GRACE_MS) {
      return;
    }
    let closed = false;
    try { closed = popup.closed; } catch (e) { closed = false; }
    if (closed) {
      // Closing the window IS the cancel gesture, so it gets no error message —
      // the same read as appleIdentity's isUserCancelled. It still refreshes,
      // because the user may have closed the window a moment after the server
      // committed the link, and a bind that worked must not look like it did
      // nothing.
      this.settleBind(null);
    }
  }

  giveUpBind() {
    this.settleBind(null);
  }

  /** Cancel button: stop waiting and take the popup with us. */
  handleCancelBind() {
    this.settleBind(null);
  }

  /**
   * End the round trip exactly once, whichever channel reported it.
   *
   * `result` is null for a cancellation (say nothing) or {ok, stringKey}.
   */
  settleBind(result) {
    if (this.bindSettled) {
      return;
    }
    this.bindSettled = true;
    this.teardownBindWatch();
    // Spent on success, kept on failure: a wrong password is worth a second
    // try without retyping, a used one is not worth holding on to.
    this.setState(result && result.ok
      ? { binding: false, linkPassword: '' }
      : { binding: false });
    if (result && result.stringKey) {
      this.props.root.addNotification(
        Localization.strings[result.stringKey], !!result.ok);
    }
    // Refresh in place — the point of the popup is that this dialog, and the
    // rest of the composer behind it, never went anywhere.
    if (typeof Config.ENABLE_EDCONNECT_BINDING === 'function'
        && Config.ENABLE_EDCONNECT_BINDING()) {
      this.loadEDConnectAccounts();
    }
    this.loadApiLinks();
  }

  async handleAppleLinkClicked() {
    // Runs straight off the click: Apple's popup only opens inside the user
    // gesture that requested it.
    try {
      const { identityToken, rawNonce } = await signInWithApple({
        locale: Localization.strings.getLanguage(),
      });
      const result = await AuthService.linkApple(
        identityToken, rawNonce, this.state.linkPassword);
      if (result.ok) {
        this.props.root.addNotification(Localization.strings.LINK_APPLE_DONE, true);
        this.setState({ linkPassword: '' });
        this.loadApiLinks();
      } else if (result.status === 409) {
        this.props.root.addNotification(Localization.strings.LINK_APPLE_CONFLICT, false);
      } else if (result.status === 403) {
        this.props.root.addNotification(Localization.strings.LINK_PASSWORD_REQUIRED, false);
      } else {
        this.props.root.addNotification(Localization.strings.LINK_APPLE_FAILED, false);
      }
    } catch (e) {
      if (isUserCancelled(e)) {
        return;
      }
      console.warn('EDConnectDialog: Apple link failed', e && (e.error || e.status));
      this.props.root.addNotification(Localization.strings.LINK_APPLE_FAILED, false);
    }
  }

  /** Is this provider already linked and active on the caller's account? */
  hasActiveLink(provider) {
    return this.state.apiLinks.some((l) => l.provider === provider && l.is_active);
  }

  /**
   * Does this account already hold an EdCity link? Read from BOTH sources.
   *
   * There are two, they are loaded by different calls, and either can fail on
   * its own:
   *   - `edconnectAccounts`, the legacy cmd get_edconnect_accounts, which is
   *     what the section below renders;
   *   - `apiLinks`, GET /api/auth/links, which carries edconnect rows too (they
   *     are filtered out at render time, not at load).
   *
   * OR, not AND, and that is the point. If the legacy list 404s or times out,
   * loadEDConnectAccountsFail resets it to empty and the dialog would otherwise
   * show the empty state with a live bind button to an account that IS linked —
   * failing OPEN on exactly the rule we are enforcing. Consulting both means a
   * single failed request can no longer produce that. The cost of a false
   * positive is one hidden button on a dialog the teacher can reopen; the cost
   * of a false negative is a doomed EdCity round trip and a refusal.
   */
  edconnectLinked() {
    const legacy = Array.isArray(this.state.edconnectAccounts)
      && this.state.edconnectAccounts.length > 0;
    return legacy || this.hasActiveLink('edconnect');
  }

  handleRemoveAccount(accountId) {
    // Show confirmation dialog with proper title/content via .show()
    if (this.props.root && this.props.root.refs && this.props.root.refs.confirmDialog) {
      this.props.root.refs.confirmDialog.show(
        {
          title: Localization.strings.EDCONNECT_UNBIND_ACCOUNT,
          content: Localization.strings.EDCONNECT_CONFIRM_REMOVE,
          confirm: Localization.strings.CONFIRM,
          cancel: Localization.strings.CANCEL,
        },
        () => {
          this.removeAccount(accountId);
        },
        null
      );
    }
  }

  removeAccount(accountId) {
    this.setState({ loading: true });
    this.props.root.refs.API.request(
      "unbind_edconnect", 
      { account_id: accountId }, 
      this.removeAccountOK, 
      this.removeAccountFail
    );
  }

  removeAccountOK(res) {
    this.setState({ loading: false });
    this.props.root.addNotification(Localization.strings.EDCONNECT_UNBIND_SUCCESS, true);
    // BOTH lists, not just the legacy one. edconnectLinked() reads the union of
    // `edconnectAccounts` and `apiLinks` so that one failed request cannot
    // fail OPEN and offer a bind to an account that already has a link — which
    // means a stale `apiLinks` fails CLOSED just as hard: the unbind leaves a
    // row in it still marked is_active, the predicate stays true forever, and
    // the bind button stays disabled on an account that now has NO link. The
    // user unbinds to switch EdCity accounts and is then stranded, unable to
    // bind the new one, with no way out but a reload.
    //
    // Found end-to-end on the deployed build, not by a unit test: both lists
    // are mocked independently in tests, so nothing noticed that only one of
    // them was being refreshed.
    this.loadEDConnectAccounts();
    this.loadApiLinks();
  }

  removeAccountFail(err) {
    this.setState({ loading: false });
    this.props.root.addNotification(Localization.strings.EDCONNECT_UNBIND_FAILED, false);
  }

  handleClose() {
    this.props.onClose();
  }

  formatAccountInfo(account) {
    // Display name or email or profile_id as fallback
    return account.display_name || account.email || account.profile_id || 'EDConnect Account';
  }

  formatAccountDetails(account) {
    const details = [];
    if (account.email) details.push(account.email);
    if (account.profile_id) details.push(`ID: ${account.profile_id}`);
    if (account.linked_at) {
      const linkedDate = new Date(account.linked_at);
      details.push(`Linked: ${linkedDate.toLocaleDateString()}`);
    }
    return details.join(' • ');
  }

  formatAccountType(account) {
    if (account.profile_type) {
      return account.profile_type.charAt(0).toUpperCase() + account.profile_type.slice(1);
    }
    return 'EDConnect';
  }

  /**
   * What the dialog shows while the EdCity window is open.
   *
   * It says where the sign-in went — a window that opens behind the browser, or
   * on another monitor, is otherwise indistinguishable from nothing happening —
   * and it offers a way out. The Cancel matters most in the one case this
   * cannot recover on its own: an OAuth state that expired while the teacher
   * was typing, which lands the popup on the API host's own failure page, a
   * different origin that can neither signal us nor be closed from here.
   */
  renderBindWaiting() {
    return (
      <Box mt={2}>
        <Typography variant="body2" color="text.secondary">
          {Localization.strings.EDCONNECT_BIND_WAITING}
        </Typography>
        <Button
          size="small"
          onClick={this.handleCancelBind}
          style={{ textTransform: 'none' }}
        >
          {Localization.strings.CANCEL}
        </Button>
      </Box>
    );
  }

  /**
   * The password box, shared by EVERY linking action in this dialog.
   *
   * It used to live inside the Google/Apple block, gated on
   * `canLinkGoogle || canLinkApple`. Both providers are off in this build, so
   * the field never rendered at all - and the EdConnect bind button, which
   * reads the SAME state, therefore always sent an empty password to a route
   * that requires one. Rendered once, above the first action that needs it, is
   * what keeps the field and its consumers from drifting apart again.
   */
  renderLinkPassword() {
    return (
      <Box mb={3}>
        <Typography variant="body2" color="text.secondary" paragraph>
          {Localization.strings.LINK_PASSWORD_HINT}
        </Typography>
        <TextField
          type="password"
          size="small"
          fullWidth
          autoComplete="current-password"
          label={Localization.strings.LINK_PASSWORD_LABEL}
          value={this.state.linkPassword}
          onChange={(e) => this.setState({ linkPassword: e.target.value })}
        />
      </Box>
    );
  }

  render() {
    const { open } = this.props;
    const { loading, binding, edconnectAccounts, hasAccounts } = this.state;
    const showEdc = typeof Config.ENABLE_EDCONNECT_BINDING === 'function'
      && Config.ENABLE_EDCONNECT_BINDING();
    const showGoogleLink = typeof Config.ENABLE_GOOGLE_BINDING === 'function'
      && Config.ENABLE_GOOGLE_BINDING();
    const showAppleLink = typeof Config.ENABLE_APPLE_LOGIN === 'function'
      && Config.ENABLE_APPLE_LOGIN();
    // "Offer the button" = the provider is enabled AND not already linked. One
    // account may hold at most one identity per provider (the table's UNIQUE is
    // on (provider, profile_id), and a second link for the same provider would
    // just be a second row nobody can tell apart in the UI).
    const canLinkGoogle = showGoogleLink && !this.hasActiveLink('google');
    const canLinkApple = showAppleLink && !this.hasActiveLink('apple');
    // EdConnect now follows the same rule the other two have always followed —
    // at most one identity per provider — enforced server-side since
    // 2026-09-17. It was the odd one out only because its rows render from the
    // separate legacy list instead of from apiLinks, so hasActiveLink was never
    // consulted for it. edconnectLinked() reads both.
    const canBindEdconnect = showEdc && !this.edconnectLinked();
    // EdConnect rows have their own section above, so they must not count here
    // — the same filter the list below applies. API.jsx OK-path convention:
    // apiLinks can be null on a 200, so check the shape before reading it.
    const hasOtherLinks = Array.isArray(this.state.apiLinks)
      && this.state.apiLinks.some((l) => l && l.provider !== 'edconnect');
    // Every link route re-proves the password, EdConnect included, so the box
    // belongs to any action that can start one - not to the Google/Apple pair
    // it happened to be nested under. `!== false` rather than `=== true`: an
    // API that does not report the flag still gets the field, because the one
    // account that genuinely has nothing to confirm (SSO-provisioned,
    // has_password false) cannot reach this dialog at all - the menu item that
    // opens it is hidden for exactly that case.
    // canBindEdconnect rather than showEdc: the box belongs to actions that can
    // actually START a link, and once the account holds its one EdCity link
    // there is no such action left on the EdConnect half. Leaving `showEdc`
    // here would render a password field for a fully-linked account with Google
    // and Apple off — a control with nothing to confirm.
    const showLinkPassword = this.props.hasPassword !== false
      && (canBindEdconnect || canLinkGoogle || canLinkApple);

    return (
      <Dialog
        open={open}
        onClose={this.handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            minHeight: '300px',
          },
        }}
      >
        <DialogTitle 
          style={{ 
            backgroundColor: this.props.theme?.palette?.primary?.main || '#ad1179', 
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <LinkIcon />
          {showEdc
            ? Localization.strings.EDCONNECT_BINDING
            : Localization.strings.LINKED_SIGNIN_METHODS}
        </DialogTitle>

        <DialogContent style={{ padding: '24px', minHeight: '200px' }}>
          {loading ? (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="150px">
              <CircularProgress />
              <Typography variant="body2" style={{ marginTop: '16px' }}>
                {Localization.strings.EDCONNECT_LOADING_ACCOUNTS}
              </Typography>
            </Box>
          ) : (
            <Box>
              {showLinkPassword && this.renderLinkPassword()}
              {/* Legacy EdConnect half — its bind button redirects to the dead
                  PHP host, so it stays hidden until the OAuth port ships. */}
              {showEdc && (hasAccounts ? (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {Localization.strings.EDCONNECT_LINKED_ACCOUNT}
                  </Typography>
                  
                  {edconnectAccounts.map((account, index) => (
                    <Paper
                      key={index}
                      elevation={1}
                      style={{
                        padding: '16px',
                        marginBottom: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      <Box display="flex" alignItems="center" gap="12px">
                        <AccountIcon color="primary" />
                        <Box>
                          <Typography variant="body1" fontWeight="bold">
                            {this.formatAccountInfo(account)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {this.formatAccountDetails(account)}
                          </Typography>
                          {/* <Box display="flex" gap="8px" mt={1}>
                            <Chip 
                              icon={<LinkIcon />} 
                              label={Localization.strings.EDCONNECT_ACCOUNT_LINKED} 
                              color="success" 
                              size="small" 
                            />
                            <Chip 
                              label={this.formatAccountType(account)} 
                              size="small" 
                              variant="outlined"
                            />
                          </Box> */}
                        </Box>
                      </Box>
                      
                      <IconButton
                        onClick={() => this.handleRemoveAccount(account.id)}
                        color="error"
                        title={Localization.strings.EDCONNECT_UNBIND_ACCOUNT}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Paper>
                  ))}
                  
                  {/* No "add another" button. One EdCity account per
                      EduVenture-AI account (server rule, 2026-09-17), so the
                      only way to change which one is linked is to remove the
                      row above and bind again — and a control that always
                      failed would be worse than no control. The sentence says
                      what to do instead, because "the button is gone" is not
                      an instruction.

                      canBindEdconnect is still consulted rather than assumed
                      false: this branch renders whenever the legacy list is
                      non-empty, while the predicate also sees apiLinks, and
                      letting the two disagree silently is how the button would
                      come back for a half-loaded dialog. */}
                  {canBindEdconnect ? (
                    <Box mt={2}>
                      <Button
                        variant="outlined"
                        startIcon={binding
                          ? <CircularProgress size={18} color="inherit" />
                          : <AddIcon />}
                        onClick={this.handleAddAccount}
                        disabled={binding}
                        fullWidth
                        // MUI uppercases button labels by default, which turns
                        // the provider's name into "EDCONNECT".
                        style={{ textTransform: 'none' }}
                      >
                        {Localization.strings.EDCONNECT_ADD_ACCOUNT}
                      </Button>
                      {binding && this.renderBindWaiting()}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" mt={2}>
                      {Localization.strings.EDCONNECT_ONE_ACCOUNT_ONLY}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Box textAlign="center" py={4}>
                  <LinkOffIcon 
                    style={{ 
                      fontSize: '64px', 
                      color: '#ccc', 
                      marginBottom: '16px' 
                    }} 
                  />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    {Localization.strings.EDCONNECT_ACCOUNT_NOT_LINKED}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {Localization.strings.EDCONNECT_BIND_HINT}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={binding
                      ? <CircularProgress size={18} color="inherit" />
                      : <LinkIcon />}
                    onClick={this.handleAddAccount}
                    // canBindEdconnect, not just `binding`. This branch renders
                    // when the LEGACY list is empty, which also happens when
                    // that call simply failed (loadEDConnectAccountsFail resets
                    // it) — and then a linked account would be shown an empty
                    // state with a live bind button, failing open on the very
                    // rule being enforced. The predicate also reads apiLinks,
                    // so one failed request can no longer do that.
                    disabled={binding || !canBindEdconnect}
                    size="large"
                    // MUI uppercases button labels by default, which turns
                    // the provider's name into "EDCONNECT".
                    style={{ textTransform: 'none' }}
                  >
                    {Localization.strings.EDCONNECT_BIND_ACCOUNT}
                  </Button>
                  {binding && this.renderBindWaiting()}
                </Box>
              ))}

              {/* Shown when a provider is linkABLE, or when the account already
                  HOLDS such a link. The second half matters when a provider is
                  switched off: gating purely on the flags would hide a Google
                  link somebody already made, leaving an active identity on the
                  account that they can neither see nor unbind. No linkable
                  provider and no existing link renders nothing at all, so the
                  section never appears as a heading with nothing under it. */}
              {(showGoogleLink || showAppleLink || hasOtherLinks) && (
                <Box mt={3}>
                  <Divider style={{ marginBottom: '16px' }} />
                  <Typography variant="h6" gutterBottom>
                    {Localization.strings.LINKED_SIGNIN_METHODS}
                  </Typography>

                  {this.state.apiLinks
                    // EdConnect rows are shown by the section above, off the
                    // legacy cmds. Listing them here as well would show the same
                    // link twice with two different unbind buttons.
                    .filter((l) => l.provider !== 'edconnect')
                    .map((link) => (
                      <Paper
                        key={'api-' + link.id}
                        elevation={1}
                        style={{
                          padding: '16px',
                          marginBottom: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          opacity: link.is_active ? 1 : 0.5,
                        }}
                      >
                        <Box display="flex" alignItems="center" gap="12px">
                          <AccountIcon color={link.is_active ? 'primary' : 'disabled'} />
                          <Box>
                            <Typography variant="body1" fontWeight="bold">
                              {/* A lookup, not `google ? 'Google' : 'Apple'` —
                                  that older form labelled EVERY non-Google row
                                  "Apple", so any third provider would have been
                                  silently mislabelled. */}
                              {(PROVIDER_LABELS[link.provider] || link.provider)
                                + (link.display_name ? ' — ' + link.display_name : '')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {link.is_active
                                ? 'ID: ' + link.profile_id
                                : Localization.strings.LINK_INACTIVE}
                            </Typography>
                          </Box>
                        </Box>
                        {Boolean(link.is_active) && (
                          <IconButton
                            onClick={() => this.handleUnlinkApi(link.id)}
                            color="error"
                            title={Localization.strings.LINK_REMOVE}
                          >
                            <LinkOffIcon />
                          </IconButton>
                        )}
                      </Paper>
                    ))}

                  {/* The two buttons are gated INDEPENDENTLY. Gating the whole
                      block on "Google is not linked" — which is what this used
                      to do — hid the Apple button the moment a teacher linked
                      Google, making the second provider unreachable. The
                      password box these need is above, shared with the
                      EdConnect bind, which requires exactly the same re-auth. */}
                  {(canLinkGoogle || canLinkApple) && (
                    <Box mt={2}>
                      <Box
                        style={{
                          display: 'flex', flexDirection: 'column',
                          alignItems: 'center', gap: '10px',
                        }}
                      >
                        {/* GIS renders the official Google button in here.
                            The container must stay MOUNTED whenever Google is
                            linkable — componentDidUpdate renders into the ref
                            once and never re-runs. */}
                        <div
                          ref={this.googleLinkContainer}
                          style={{ display: canLinkGoogle ? 'block' : 'none' }}
                        />
                        {canLinkApple && (
                          <AppleSignInButton
                            onClick={this.handleAppleLinkClicked}
                            label={Localization.strings.LINK_WITH_APPLE}
                          />
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions style={{ padding: '16px 24px' }}>
          <Button onClick={this.handleClose}>
            {Localization.strings.CLOSE}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

export default EDConnectDialog;
